import { useState, useEffect, useRef, useCallback } from 'react';
import * as listRepository from '../repositories/listRepository';

interface ListPickerProps {
  articleId: string;
  open: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
}

export function IconList() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6"/>
      <line x1="8" y1="12" x2="21" y2="12"/>
      <line x1="8" y1="18" x2="21" y2="18"/>
      <line x1="3" y1="6" x2="3.01" y2="6"/>
      <line x1="3" y1="12" x2="3.01" y2="12"/>
      <line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  );
}

export function ListPicker({ articleId, open, onClose, triggerRef }: ListPickerProps) {
  const [allLists, setAllLists] = useState<string[]>([]);
  const [articleLists, setArticleLists] = useState<string[]>([]);
  const [newListName, setNewListName] = useState('');
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [all, current] = await Promise.all([
      listRepository.getUserLists(),
      listRepository.getArticleLists(articleId),
    ]);
    setAllLists(all);
    setArticleLists(current);
    setLoading(false);
  }, [articleId]);

  useEffect(() => {
    if (open) {
      load();
      setNewListName('');
    }
  }, [open, load]);

  useEffect(() => {
    if (open && !loading) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, loading]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef?.current?.contains(target)) return;
      if (ref.current && !ref.current.contains(target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, triggerRef]);

  const toggleList = async (tag: string) => {
    const isIn = articleLists.includes(tag);
    if (isIn) {
      setArticleLists((prev) => prev.filter((t) => t !== tag));
      await listRepository.removeArticleFromList(articleId, tag);
    } else {
      setArticleLists((prev) => [...prev, tag]);
      await listRepository.addArticleToList(articleId, tag);
      if (!allLists.includes(tag)) {
        setAllLists((prev) => [...prev, tag].sort());
      }
    }
  };

  const handleCreateAndAdd = async () => {
    const tag = newListName.trim();
    if (!tag || tag.length > listRepository.LIST_NAME_MAX_LENGTH) return;
    setNewListName('');
    await toggleList(tag);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateAndAdd();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
    e.stopPropagation();
  };

  if (!open) return null;

  return (
    <div className="list-picker" ref={ref}>
      {loading ? (
        <div className="list-picker-loading">Loading...</div>
      ) : (
        <>
          {allLists.length > 0 && (
            <div className="list-picker-items">
              {allLists.map((tag) => (
                <label key={tag} className="list-picker-item">
                  <input
                    type="checkbox"
                    checked={articleLists.includes(tag)}
                    onChange={() => toggleList(tag)}
                  />
                  <span>{tag}</span>
                </label>
              ))}
            </div>
          )}
          <div className="list-picker-new">
            <input
              ref={inputRef}
              type="text"
              placeholder="New list..."
              value={newListName}
              maxLength={listRepository.LIST_NAME_MAX_LENGTH}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {newListName.trim() && (
              <button className="list-picker-add-btn" onClick={handleCreateAndAdd}>
                Add
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
